// ─── Membership (Camino TMAI) — domain types ────────────────────────────────
// R0 scope: everything inside the tenant. No networks/*, no cross-tenant reads
// or writes. The readiness engine mirrors `computeCapComplianceAudit` (pure fn +
// versioned framework + contract test); this file is the analogue of
// `cap.entity.ts`'s framework/report shapes. `en` is authoritative for the TMAI
// packet; `es` is the UI source (reuse `I18nText` from cap.entity — do not
// redefine a bilingual type).
// ── Enumerations (const-arrays so the type derives and the contract test can
//    iterate the allowed values) ─────────────────────────────────────────────
/** Membership stage. `notStarted` is the honest default for a TC that has never
 *  touched the module — it is NOT the same as `affiliate` (an actual TMAI
 *  relationship someone affirmed). `notStarted` has NO criteria of its own and
 *  is NOT a rung of the TMAI ladder (`MembershipFramework.stages` holds only the
 *  four real stages); a TC leaves it by choosing a target stage. The four-level
 *  ladder is adopted from the signed Statement of Intent (over the Pathway's two
 *  stages — see DECISIONS.md). A criterion's `stage` is the stage the TC must
 *  COMPLETE to advance to the next one. */
export const MEMBERSHIP_STAGES = [
    'notStarted',
    'affiliate',
    'preCandidate',
    'candidate',
    'member',
];
/** El escalón siguiente, o `null` si ya está arriba de todo. El orden ES el del
 *  array: la escalera no se declara dos veces. */
export function nextStage(current) {
    const i = MEMBERSHIP_STAGES.indexOf(current);
    return MEMBERSHIP_STAGES[i + 1] ?? null;
}
/** Posición en la escalera. Para comparar sin repartir el orden por el código. */
export function stageRank(stage) {
    return MEMBERSHIP_STAGES.indexOf(stage);
}
// ── Movimientos en la escalera ──────────────────────────────────────────────
// La etapa ALCANZADA la declara la red (TMAI), nunca el seminario — igual que la
// afiliación. Ver DECISIONS 2026-08-05.
/** Qué ACTO produjo un cambio de escalón. No es "de dónde viene el escalón" sino
 *  "qué pasó", que es lo que una auditoría necesita distinguir:
 *  - `earned`      la plataforma lo derivó de una submission aprobada;
 *  - `recognized`  TMAI afirma un escalón ganado FUERA de la plataforma (un TC
 *                  que ya era Miembro antes de existir el módulo), o corrige;
 *  - `revoked`     TMAI bajó de escalón por incumplimiento. */
export const STAGE_CHANGE_KINDS = ['earned', 'recognized', 'revoked'];
/** Desde cuándo cuenta ESTE escalón. Explícito por kind, sin `??`: en `recognized`
 *  la fecha efectiva es obligatoria y es la que vale; en `earned` y `revoked` el
 *  acto y su efecto son el mismo instante. */
export function stageEffectiveAt(change) {
    return change.kind === 'recognized' ? change.effectiveAt : change.at;
}
/** Cómo llegó el seminario al escalón que tiene hoy. TRES valores, no dos.
 *
 *  "Está en Candidato porque TMAI le bajó de Miembro" es una respuesta legítima a
 *  "¿cómo llegó a este escalón?" — y perderla caminando hacia atrás hasta el
 *  último `earned`/`recognized` sería borrar el hecho más importante de su
 *  historia. Derivado, NO almacenado: guardarlo aparte sería un segundo campo
 *  describiendo la misma verdad que ya está en la historia, en el mismo
 *  documento. */
export function deriveStageOrigin(history) {
    return history.length === 0 ? null : (history[history.length - 1].kind ?? null);
}
/** Qué se COPIA al espejo del seminario y qué se omite.
 *
 *  El espejo es una segunda copia y la redacción tiene el mismo problema que el
 *  packet de la submission: agregar un campo sensible a `StageChange` lo filtraría
 *  en silencio. `satisfies Record<keyof StageChange, …>` hace que agregar un campo
 *  NO COMPILE hasta decidir explícitamente si viaja. Hoy `byUid` es el único
 *  campo sensible; el próximo entraría sin que nadie lo note. */
export const STAGE_CHANGE_MIRROR_POLICY = {
    stage: 'mirror',
    previousStage: 'mirror',
    kind: 'mirror',
    at: 'mirror',
    reason: 'mirror',
    submissionId: 'mirror',
    // El seminario TIENE que ver desde cuándo cuenta su etapa: es el dato del que
    // depende su plazo de candidatura. Ocultarlo dejaría el reloj sin explicación.
    effectiveAt: 'mirror',
    // El seminario ve QUÉ pasó y CUÁNDO, nunca QUIÉN lo decidió — la misma
    // asimetría que el espejo de las submissions, que lleva `observations` y
    // `decidedAt` pero jamás `decidedByUid`.
    byUid: 'omit',
};
/** Copia campo por campo según la política. Whitelist, no denylist: un campo
 *  nuevo no viaja hasta que alguien lo agregue acá Y al mapa. */
export function mirrorStageChange(change) {
    const out = {
        stage: change.stage,
        previousStage: change.previousStage,
        kind: change.kind,
        at: change.at,
    };
    if (change.reason !== undefined)
        out.reason = change.reason;
    if (change.submissionId !== undefined)
        out.submissionId = change.submissionId;
    return out;
}
/** Union of BOTH source-document taxonomies — the TC must see, on the board, the
 *  same headers it sees in each signed document. The Pathway uses Theological /
 *  Academic / Administrative / Personnel; the Statement of Intent §4 uses
 *  LEADERSHIP / FINANCES / DOCTRINAL / ADMINISTRATIVE / ACADEMIC PROGRAM /
 *  VISION & MISSION FOR MATURITY. `administrative` is shared (same header in both).
 *  `doctrinal`/`academicProgram` are deliberately NOT collapsed into
 *  `theological`/`academic` — they are distinct headers in the signed doc. The
 *  render/i18n layer maps each value to its document label. */
export const CRITERION_CATEGORIES = [
    // Pathway taxonomy
    'theological',
    'academic',
    'administrative',
    'personnel',
    // Statement of Intent §4 taxonomy
    'leadership',
    'finances',
    'doctrinal',
    'academicProgram',
    'visionMissionMaturity',
];
/** How a criterion is EVALUATED — NOT how a single piece of evidence is captured.
 *  Two orthogonal axes. `'attestation'` is intentionally unused by the framework
 *  criteria (a doctrinal criterion is evaluated as `'derived'` from the
 *  attestation records — see DERIVED_INDICATOR_KEYS `DOCTRINAL_ATTESTATION_RATE`),
 *  but it STAYS a valid `EvidenceKind` because `MembershipEvidence.kind` uses it:
 *  an individual evidence item CAN be an attestation. Do not "clean it up". */
export const EVIDENCE_KINDS = ['derived', 'upload', 'attestation', 'declaration'];
export const CRITERION_STATUS_CODES = ['met', 'partial', 'not_met', 'not_applicable'];
/** Auto-derived indicators — a criterion may reference one ONLY when
 *  `evidenceKind === 'derived'` (contract-test invariant). Every key here has a
 *  verified data source in the repo today, EXCEPT `DOCTRINAL_ATTESTATION_RATE`,
 *  which R0-PR3 makes derivable by creating the attestations collection.
 *
 *  NOTE: `COURSE_OFFERINGS_HORIZON` is intentionally absent — the raw term/section
 *  dates exist, but a 4-month horizon is the TC's real gap, not a broken metric.
 *  It is modeled as `evidenceKind: 'upload'` so it never breaks the
 *  `derivedIndicator ⟺ derived` invariant. See BACKLOG.md. */
export const DERIVED_INDICATOR_KEYS = [
    'CCD_COVERAGE', // % of COURSES of the catalog that have a CCD
    'PROGRAM_CURRICULUM_DOCUMENTED', // programs with a curriculumVersion whose terms are populated
    'PROGRAM_MISSION_DEFINED', // programs with a purpose/mission statement (Program.purpose)
    'PROGRAM_DESCRIPTION_DOCUMENTED', // programs with a description (Program.description) — distinct field from purpose
    'FACULTY_ROSTER_SIZE', // count of people with a faculty/docente role
    'ENROLLED_STUDENT_COUNT', // aggregate active enrollments, no PII
    'GRADUATE_COUNT', // aggregate graduated enrollments, no PII
    'DOCTRINAL_ATTESTATION_RATE', // % of faculty+leadership with a current-cycle attestation
    'CAP_BSC_STATUS', // from CapComplianceReport (capCode BSC) — hinge to the CAP module
    'CAP_DEP_STATUS', // from CapComplianceReport (capCode DEP)
    // Statement of Intent §4 P3: at least one intakeTermId of the BSC program has at
    // least one ProgramEnrollment that reached `graduated`. NOT the whole intake —
    // the TC does not control attrition; TMAI wants evidence the program produces
    // graduates, not perfect retention. Source: ProgramEnrollment.intakeTermId +
    // status==='graduated'; BSC program resolved via CapComplianceReport (capCode
    // 'BSC'). Computed in PR4 — if the data contradicts this definition, flag before
    // changing it.
    'BSC_COHORT_GRADUATED',
];
export const MEMBERSHIP_MILESTONE_KEYS = [
    'statementOfIntentSigned',
    'applicationSubmitted',
    'preCandidateApproved',
    'candidateApproved',
    'memberApproved',
];
/** Attestation response state. `affirmed` = signed without reservation;
 *  `affirmed_with_explanation` = the person has a reservation and, per SIO §4 D1,
 *  sends a written explanation DIRECTLY to TMAI — the platform NEVER stores that
 *  text (it would insert the employer/tenantAdmin into a channel TMAI designed as
 *  person→TMAI, and expose the person's conscience to their employer). `pending`
 *  is a COMPUTED state for a roster member with no attestation this cycle — it is
 *  never persisted (there is no "save pending" flow). Stored docs are only
 *  `affirmed` | `affirmed_with_explanation`. */
export const ATTESTATION_STATUSES = ['affirmed', 'affirmed_with_explanation', 'pending'];
/** Immutable snapshot — aggregates only, zero PII (mirrors CapComplianceReport). */
/** Per-criterion outcome IN THE READINESS SNAPSHOT — distinct from the manual
 *  `CriterionStatusCode`. Adds `no_data`: the derived indicator's SOURCE SET is
 *  empty / a prerequisite artifact is absent (e.g. no CAP audit was ever run), so
 *  the criterion cannot be EVALUATED. `no_data` ≠ `not_met` ("cannot evaluate" is
 *  not "does not comply") — the render layer shows "sin datos para evaluar". A
 *  gating criterion in `no_data` still blocks `ready` (unproven ≠ ready). */
export const READINESS_OUTCOMES = [
    'met',
    'partial',
    'not_met',
    'not_applicable',
    'no_data',
];
/** Warning codes (NOT text — the render layer resolves `membership:*`). */
export const MEMBERSHIP_WARNING_CODES = {
    DERIVED_NO_DATA: 'MEMBERSHIP_DERIVED_NO_DATA',
    /** Criterio MANUAL que nadie respondió. Distinto de DERIVED_NO_DATA: allí
     *  falta el dato de origen, acá falta la respuesta del TC. Mismo estado
     *  (`no_data`), distinto motivo — y por eso distinto texto en la UI. */
    MANUAL_NO_DATA: 'MEMBERSHIP_MANUAL_NO_DATA',
    /** La etapa evaluada no tiene NINGÚN criterio gating, así que `ready` no
     *  afirma que se verificó algo: afirma que no había nada que verificar. Sin
     *  esto, el vacío se leería como aprobación. */
    NO_GATING_CRITERIA: 'MEMBERSHIP_NO_GATING_CRITERIA',
    NOT_APPLICABLE_NO_NOTE: 'MEMBERSHIP_NOT_APPLICABLE_NO_NOTE',
    DECLARATION_UNMET: 'MEMBERSHIP_DECLARATION_UNMET',
    GATING_UNMET: 'MEMBERSHIP_GATING_UNMET',
};
// ─── El reloj de candidatura ────────────────────────────────────────────────
// Dos años desde que el TC ENTRA en candidatura. DERIVADO de la historia, jamás
// almacenado: un campo aparte sería una segunda verdad sobre el mismo hecho, en
// el mismo documento — la forma que ya nos mordió con `createdAt`/`occurredAt`.
/** Duración normativa de la candidatura. */
export const CANDIDACY_TERM_YEARS = 2;
/** DE DÓNDE ARRANCA EL RELOJ — el único punto que lo decide.
 *
 *  Hoy: de la entrada de historia que puso al TC en `candidate`, o sea de la
 *  APROBACIÓN de TMAI. No de la presentación: la solicitud de candidatura la
 *  presenta un PRE-candidato, y si el reloj corriera desde `submittedAt`
 *  empezaría antes de que TMAI acepte —incluso si nunca acepta— y el TC perdería
 *  de sus dos años el tiempo que la red se tomó en responder.
 *
 *  ⚠️ ESTÁ AISLADO A PROPÓSITO. Hay una pregunta abierta a TMAI sobre si el plazo
 *  corre desde la presentación o desde el ingreso efectivo. Si responden "desde
 *  la presentación", lo que cambia es ESTA función y nada más: el llamador le
 *  pasa la ronda de candidatura y se devuelve su `submittedAt`. Por eso recibe
 *  ambas cosas aunque hoy solo use una. */
function candidacyStart(entry, _candidacyRound) {
    return stageEffectiveAt(entry);
}
/** El plazo de candidatura, o `null` si NO CORRESPONDE que haya uno.
 *
 *  EL ESCALÓN ACTUAL MANDA, no la historia. Un TC que ya es Miembro pasó por
 *  candidatura —su historia conserva esa entrada para siempre— y calcular su
 *  vencimiento le mostraría un plazo VENCIDO a alguien que ya cumplió. Lo mismo
 *  con uno que bajó de etapa. El reloj corre mientras se es candidato; fuera de
 *  ahí no devuelve un plazo pasado: no devuelve nada.
 *
 *  Se toma la ÚLTIMA entrada a `candidate`: si el TC entró, salió y volvió a
 *  entrar, su plazo cuenta desde la vez que está corriendo, no desde la primera. */
export function candidacyClock(achievedStage, history, now, candidacyRound) {
    if (achievedStage !== 'candidate')
        return null;
    const entry = [...history].reverse().find((h) => h.stage === 'candidate' && h.kind !== 'revoked');
    if (!entry)
        return null;
    const startedAt = candidacyStart(entry, candidacyRound);
    const expires = new Date(startedAt);
    expires.setUTCFullYear(expires.getUTCFullYear() + CANDIDACY_TERM_YEARS);
    const expiresAt = expires.toISOString();
    const daysRemaining = Math.floor((expires.getTime() - Date.parse(now)) / 86_400_000);
    return { startedAt, expiresAt, daysRemaining };
}
//# sourceMappingURL=membership.entity.js.map